/**
 * fieldNames — unique submitted names for input widgets.
 *
 * A form is its section grid, and publishing refuses two fields with the same
 * name in one form. So a name is allocated when an input lands in a grid (a
 * drop, a duplicate, a move), against the inputs that grid holds at that
 * moment — never in advance, when the destination is not known.
 */
import { subscribeGridsCommitted, useGridStore } from "@fredericrous/grid";

import { patchContentProps } from "./pendingEdits.js";

const PREFIX = "field_";

/** The smallest `field_<n>` no input of `gridId` uses as its name. */
export function allocateFieldName(
  gridId: string,
  excludeItemId?: string,
): string {
  const items = useGridStore.getState().grids[gridId]?.items ?? {};
  const used = new Set<string>();
  for (const [id, item] of Object.entries(items)) {
    if (id === excludeItemId || item.contentType !== "input") continue;
    const name = (item.contentProps as { name?: unknown } | undefined)?.name;
    if (typeof name === "string") used.add(name);
  }
  let n = 1;
  while (used.has(`${PREFIX}${n}`)) n++;
  return `${PREFIX}${n}`;
}

/** Inputs by the grid they sit in, as of the last commit. */
function inputHomes(): Map<string, string> {
  const homes = new Map<string, string>();
  for (const [gridId, grid] of Object.entries(useGridStore.getState().grids))
    for (const [id, item] of Object.entries(grid.items))
      if (item.contentType === "input") homes.set(id, gridId);
  return homes;
}

function nameOf(gridId: string, itemId: string): string | undefined {
  const cp = useGridStore.getState().grids[gridId]?.items[itemId]
    ?.contentProps as { name?: unknown } | undefined;
  return typeof cp?.name === "string" ? cp.name : undefined;
}

/**
 * Renames an input MOVED into a grid where another input already has its
 * name (the drop and duplicate paths name theirs as they insert). Only the
 * person's own moves count: an undo, a redo or another client's change is
 * replayed as it is (that client renamed on its side). Returns the
 * unsubscribe.
 */
export function renameClashingMovedInputs(): () => void {
  let homes = inputHomes();
  return subscribeGridsCommitted(useGridStore, ({ gridIds, origin }) => {
    const before = homes;
    homes = inputHomes();
    if (origin !== "interaction" && origin !== "programmatic") return;
    const clashing: { gridId: string; itemId: string }[] = [];
    for (const gridId of gridIds) {
      const items = useGridStore.getState().grids[gridId]?.items ?? {};
      for (const itemId of Object.keys(items)) {
        const was = before.get(itemId);
        if (homes.get(itemId) !== gridId || !was || was === gridId) continue;
        const name = nameOf(gridId, itemId);
        const taken = Object.keys(items).some(
          (other) => other !== itemId && nameOf(gridId, other) === name,
        );
        if (name !== undefined && taken) clashing.push({ gridId, itemId });
      }
    }
    // After the event: a write from inside a commit listener would nest a
    // commit in the one being reported.
    if (clashing.length)
      queueMicrotask(() => {
        for (const { gridId, itemId } of clashing)
          patchContentProps(gridId, itemId, {
            name: allocateFieldName(gridId, itemId),
          });
      });
  });
}
